import React, { useRef, useState } from 'react';
import { Camera, Trash2, Upload } from 'lucide-react';
import { api } from '../lib/api';
import { usePhotos, usePhotoMutations } from '../hooks/useGardenData';
import { formatDate } from '../lib/utils';
import { Button } from './ui/Button';

interface PhotoTimelineProps {
  entityType: 'plant' | 'bed';
  entityId: string;
}

export function PhotoTimeline({ entityType, entityId }: PhotoTimelineProps) {
  const { data: photos = [], isLoading } = usePhotos(entityType, entityId);
  const { upload, remove } = usePhotoMutations(entityType, entityId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await upload.mutateAsync({ file, caption: caption || undefined });
      setCaption('');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (isLoading) return <div className="text-sm text-gray-400 py-2">Loading photos…</div>;

  return (
    <div className="flex flex-col gap-3">
      {/* Upload strip */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          aria-label="Photo caption"
          className="flex-1 text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:outline-none focus:border-garden-400 placeholder:text-gray-400 dark:placeholder:text-gray-500"
        />
        <Button
          variant="outline"
          size="sm"
          loading={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={14} />
          Add Photo
        </Button>
        <input
          type="file"
          accept="image/*"
          aria-label="Upload photo"
          className="hidden"
          ref={fileRef}
          onChange={handleFileChange}
        />
      </div>

      {photos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
          <Camera size={28} />
          <p className="text-sm">No photos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group rounded-lg overflow-hidden bg-gray-100 aspect-video">
              <img
                src={api.photoUrl(photo.r2_key)}
                alt={photo.caption ?? photo.filename}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-xs truncate">{photo.caption || photo.filename}</p>
                <p className="text-white/70 text-xs">{formatDate(photo.date_taken ?? photo.created_at)}</p>
              </div>
              <button
                onClick={() => remove.mutate(photo.id)}
                aria-label={`Delete photo${photo.caption ? `: ${photo.caption}` : ''}`}
                className="absolute top-1.5 right-1.5 bg-black/50 text-white rounded-full p-1 opacity-40 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
